import Protektor from '@iris-platform/protektor';
import {
  compose,
  equals,
  length,
  innerJoin,
  keysIn,
  valuesIn,
  map,
  zipObj,
  __,
  not,
} from 'ramda';
import { resolver } from '../util';
import { RolesError } from '../errors';
import config from '../../config';

// Given list of fields expected in query (keys in object)
// determine if all of them are present in query object.
const isPartialQuery = (expectedKeys, query) => compose(
  not,
  equals(length(expectedKeys)),
  length,
  innerJoin((key, name) => key === name, keysIn(query)),
)(expectedKeys);

const prefixQueryFields = (prefix, query) => compose(
  zipObj(__, valuesIn(query)),
  map(key => `${prefix}${key}`),
  keysIn,
)(query);

const roleIdentifierKeys = ['name', 'team', 'displayName'];

const partialQueryPrefix = 'roleIdentifier.';

const allRoles = resolver.authenticatedResolver.createResolver(
  () => Protektor.filterRoles(() => true, true),
);

const allRolesPaginated = resolver.authenticatedResolver.createResolver(
  async (root, { filter, skip, limit }) => {
    const roles = await Protektor.filterRoles(() => true, true);
    const filteredRoles = (filter) ? roles.filter(role => (
      role.roleIdentifier.name.includes(filter)
      || role.roleIdentifier.displayName.includes(filter)
    )) : roles;
    const startIndex = (skip && skip > 0) ? skip : 0;
    const endIndex = (limit && limit > 0) ? skip + limit : filteredRoles.length;
    return {
      roles: filteredRoles.slice(startIndex, endIndex),
      count: filteredRoles.length,
    };
  },
);

const allTeamRoles = resolver.authenticatedResolver.createResolver(
  async (root, { teamID }) => {
    const roles = await Protektor.filterRoles(() => true, true);
    const teamRoles = [];
    roles.forEach((role) => {
      if (role.roleIdentifier.team === teamID) {
        teamRoles.push(role);
      }
    });
    return teamRoles;
  },
);

const role = resolver.authenticatedResolver.createResolver(
  async (root, { roleIdentifier }) => {
    const query = isPartialQuery(roleIdentifierKeys, roleIdentifier)
      ? prefixQueryFields(partialQueryPrefix, roleIdentifier)
      : roleIdentifier;
    let foundRole;

    try {
      foundRole = await Protektor.roleToJSON(query);
    } catch (err) {
      return RolesError(err);
    }

    return foundRole;
  },
);

const allow = resolver.authenticatedResolver.createResolver(
  async (root, { action, resource, roleIdentifier }) => {
    try {
      await Protektor.allow({ action, resource, roleIdentifier });
      return { action, resource, allowed: true };
    } catch (err) {
      return RolesError(err);
    }
  },
);

const forbid = resolver.authenticatedResolver.createResolver(
  async (root, { action, resource, roleIdentifier }) => {
    try {
      await Protektor.forbid({ action, resource, roleIdentifier });
      return { action, resource, allowed: false };
    } catch (err) {
      return RolesError(err);
    }
  },
);

const createEmptyRole = resolver.authenticatedResolver.createResolver(
  async (root, { roleIdentifier }) => {
    try {
      const action = '__reserved__';
      const resource = '__reserved__';
      await Protektor.forbid({ action, resource, roleIdentifier });
      const foundRole = await Protektor.roleToJSON(roleIdentifier);
      return foundRole;
    } catch (err) {
      return RolesError(err);
    }
  },
);

const removePermission = resolver.authenticatedResolver.createResolver(
  async (root, { action, resource, roleIdentifier }) => {
    const query = isPartialQuery(roleIdentifierKeys, roleIdentifier)
      ? prefixQueryFields(partialQueryPrefix, roleIdentifier)
      : roleIdentifier;

    try {
      await Protektor.removePermission({
        action,
        resource,
        roleIdentifier: query,
      });

      return { action, resource, allowed: false };
    } catch (err) {
      return RolesError(err);
    }
  },
);

const removeRole = resolver.authenticatedResolver.createResolver(
  async (root, { roleIdentifier }) => {
    const query = isPartialQuery(roleIdentifierKeys, roleIdentifier)
      ? prefixQueryFields(partialQueryPrefix, roleIdentifier)
      : roleIdentifier;

    try {
      return Protektor.removeRole(query);
    } catch (err) {
      return RolesError(err);
    }
  },
);

const templatedRole = resolver.authenticatedResolver.createResolver(
  async (root, {
    name, team, currentName, currentTeam,
  }) => {
    let selectedRole;
    let currentRole;
    try {
      selectedRole = await Protektor.roleToJSON({
        'roleIdentifier.name': name,
        'roleIdentifier.team': team,
      });
    } catch (err) {
      return RolesError(err);
    }

    if (currentName !== undefined && currentTeam !== undefined) {
      try {
        currentRole = await Protektor.roleToJSON({
          'roleIdentifier.name': currentName,
          'roleIdentifier.team': currentTeam,
        });
      } catch (err) {
        return RolesError(err);
      }
      return {
        role: selectedRole,
        template: config.resourceModels.defaultMapping,
        currentRole,
      };
    }

    return {
      role: selectedRole,
      template: config.resourceModels.defaultMapping,
    };
  },
);

export default {
  Query: {
    allRoles,
    allRolesPaginated,
    role,
    templatedRole,
    allTeamRoles,
  },
  Mutation: {
    allow,
    forbid,
    createEmptyRole,
    removePermission,
    removeRole,
  },
};
